import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { JobArchitecture } from '~/shared/data-contract/job-architecture';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { isQuerySuccess } from './is-success-query';
import { jobArchitectureKeys } from './query-keys';

type UseJobArchitecturesOptions = {
    readonly enabled?: boolean;
    readonly refetchIntervalInMs?: number;
};

export const useJobArchitectures = (
    options?: UseJobArchitecturesOptions,
): UseQueryResult<readonly JobArchitecture[], Error> => {
    const refetchIntervalInMs = options?.refetchIntervalInMs ?? 2000;
    const { fetchJobArchitectures } = useApplicationFetchers();

    return useQuery({
        queryFn: async () => {
            const result = await fetchJobArchitectures();

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        enabled: options?.enabled ?? true,
        queryKey: jobArchitectureKeys.architectures(),
        refetchInterval: (query) => {
            if (!isQuerySuccess(query)) {
                return false;
            }

            const stillGenerating = query.state.data.some(
                (jobArchitecture) => jobArchitecture.generationInfo?.inProgress === true,
            );

            return stillGenerating ? refetchIntervalInMs : false;
        },
        throwOnError: false,
        staleTime: 10_000,
        gcTime: 10_000,
    });
};

export const useIsJobArchitectureGenerating = (jobArchitectureId: string): boolean => {
    const { data: jobArchitectures } = useJobArchitectures();

    return (
        jobArchitectures?.some(
            (jobArchitecture) =>
                jobArchitecture.id === jobArchitectureId && jobArchitecture.generationInfo?.inProgress === true,
        ) ?? false
    );
};
